<template>
  <form class="erc-vehicle-catalog-form" method="post" name="VDB">
    <div class="form-field-wrapper">
      <div class="field-group">
        <label class="" for="form-field-vehicle-type">Type:</label>
        <div class="form-field select-wrapper ">
          <select id="form-field-vehicle-type" @change="onVehicleTypeChange" v-model="form.vehicleType" name="vehicle-type">
            <option :value="null">Please select the type</option>
            <option v-for="vehicleType in vehicleTypes"
                    :key="`vehicle-type-${vehicleType.id}`"
                    :value="vehicleType.id">
              {{ vehicleType.name }}
            </option>
          </select>
        </div>
      </div>
      <div class="field-group elementor-column">
        <label class="" for="form-field-brand">Brand:</label>
        <div class="form-field select-wrapper ">
          <select id="form-field-brand" @change="onBrandChange" v-model="form.brand"
                  :disabled="!form.vehicleType || !brands"
                  name="brand">
            <option :value="null">Please select the brand</option>
            <option v-for="brand in brands"
                    :key="`brand-${brand.id}`"
                    :value="brand.id">
              {{ brand.name }}
            </option>
          </select>
        </div>
      </div>
      <div class="field-group elementor-column">
        <label class="" for="form-field-model">Model:</label>
        <div class="form-field select-wrapper ">
          <select id="form-field-model" @change="onModelChange" v-model="form.model"
                  :disabled="!form.brand || !models"
                  class="elementor-field-textual elementor-size-sm" name="model">
            <option :value="null">Please select model</option>
            <option v-for="model in models"
                    :key="`model-${model.id}`"
                    :value="model.id">
              {{ model.name }}
            </option>
          </select>
        </div>
      </div>
      <div class="field-group elementor-column ">
        <label class="" for="form-field-generation">Generation:</label>
        <div class="form-field select-wrapper ">
          <select id="form-field-generation" @change="onGenerationChange" v-model="form.generation"
                  :disabled="!form.model || !generations"
                  class="elementor-field-textual elementor-size-sm" name="generation">
            <option :value="null">Please select generation</option>
            <option v-for="generation in generations"
                    :key="`generation-${generation.id}`"
                    :value="generation.id">
              {{ generation.name }}
            </option>
          </select>
        </div>
      </div>
      <div class="field-group elementor-column">
        <label class="" for="form-field-fuel">Fuel:</label>
        <div class="form-field select-wrapper ">
          <select id="form-field-fuel" @change="onFuelChange" v-model="form.fuel"
                  :disabled="!form.generation || !fuels"
                  class="elementor-field-textual elementor-size-sm" name="fuel">
            <option v-for="fuel in fuels"
                    :key="`model-${fuel.id}`"
                    :value="fuel.id">
              {{ fuel.name }}
            </option>
          </select>
        </div>
      </div>
      <div class="field-group elementor-column ">
        <label class="" for="form-field-engine">Engine:</label>
        <div class="form-field select-wrapper ">
          <select id="form-field-engine" @change="onEngineChange" v-model="form.engine"
                  :disabled="engines.length === 0"
                  class="elementor-field-textual elementor-size-sm" name="engine">
            <option :value="null">Please select engine</option>
            <option v-for="engine in engines"
                    :key="`engine-${engine.id}`"
                    :value="engine.id">
              {{ engine.name }}
            </option>
          </select>
        </div>
      </div>
      <div class="field-group">
        <span></span></div>
      <div class="field-group field-group__button">
        <button :disabled="!isFormValid"
                class="submit-button"
                type="submit"
                @click.prevent="$emit('getRemaps', form)">
          VIEW REMAPS
        </button>
      </div>
    </div>
  </form>
</template>

<script>
import { API_BASE } from "@/constants/api";
// Assuming buildApiUrl is exported from a utility file
import { buildApiUrl } from '@/utils/apiUtils';

export default {
  name: 'VehicleCatalogForm',
  data() {
    return {
      vehicleTypes: [],
      brands: [],
      models: [],
      generations: [],
      fuels: [],
      engines: [],
      form: {
        vehicleType: null,
        brand: null,
        model: null,
        generation: null,
        fuel: null, // 'null' represents "All fuel types"
        engine: null,
      },
      selectedEngineSlug: null, // Stores the slug from the database for the selected engine
      isPrefilling: false, // Flag to manage state during initial load/prefill from URL
      // Optional: Add state for loading indicators or user-facing errors
      // isLoading: false,
      // errorMessage: null,
    }
  },
  async created() {
    this.isPrefilling = true;
    // this.isLoading = true; // Optional: Set loading state
    // this.errorMessage = null; // Optional: Clear previous errors
    try {
      await this.getVehicleTypes();
      await this.prefillFromUrl();
    } catch (error) {
      console.error("Error during component creation:", error);
      // this.errorMessage = "Failed to initialize the form. Please refresh."; // Optional
    } finally {
      this.isPrefilling = false;
      // this.isLoading = false; // Optional: Clear loading state

      // After loading/prefilling, ensure URL consistency
      if (this.form.engine && this.selectedEngineSlug) {
          this.updateVehicleSlugUrl(); // Update URL if prefill succeeded
      } else if (this.$route.query.vehicle) {
          // Clear URL slug if prefill failed or didn't select an engine
          console.log("Clearing potentially invalid slug after failed/incomplete prefill.");
          this.updateVehicleSlugUrl();
      }
    }
  },
  computed: {
    isFormValid() {
      // Form is valid for submission when an engine is selected
      return !!this.form.engine;
    },
  },
  methods: {
    /**
     * Resets form fields and data arrays from a specified level downwards.
     * @param {string} level - The level from which to reset ('vehicleType', 'brand', 'model', 'generation', 'fuel').
     */
    resetFieldsFrom(level) {
        const levels = ['vehicleType', 'brand', 'model', 'generation', 'fuel', 'engine'];
        const startIndex = levels.indexOf(level);

        if (startIndex === -1) return;

        for (let i = startIndex + 1; i < levels.length; i++) {
            const field = levels[i];
            this.form[field] = null; // Reset form value

            // Reset corresponding data arrays
            if (field === 'brand') this.brands = [];
            if (field === 'model') this.models = [];
            if (field === 'generation') this.generations = [];
            if (field === 'fuel') this.fuels = [];
            if (field === 'engine') this.engines = [];
        }
        // Always clear the stored engine slug if resetting anything below vehicleType
        if (startIndex < levels.indexOf('engine')) {
            this.selectedEngineSlug = null;
        }
        console.log(`Fields reset from level: ${level}`);
    },

    /**
     * Centralized helper for making API fetch requests.
     * @param {string} endpointPath - The specific API endpoint path.
     * @param {URLSearchParams|Object|null} params - Query parameters for the request.
     * @returns {Promise<any>} - Resolves with the JSON data or rejects on error.
     */
    async fetchData(endpointPath, params = null) {
        const url = buildApiUrl(endpointPath, params); // Use the shared helper
        // this.isLoading = true; // Optional: Set loading state
        // this.errorMessage = null; // Optional: Clear previous errors
        try {
            const response = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
            if (!response.ok) {
                // Try to get more error info from response body if possible
                let errorBody = null;
                try { errorBody = await response.json(); } catch (e) { /* ignore */ }
                console.error(`HTTP error! Status: ${response.status} for ${url}`, errorBody);
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            // Optional: Basic validation if data is always expected to be an array for dropdowns
            // if (!Array.isArray(data) && endpointPath.includes('dropdown')) { // Example check
            //     console.warn(`API response for ${endpointPath} was not an array:`, data);
            //     return []; // Return empty array to prevent errors
            // }
            return data;
        } catch (error) {
            console.error(`Error fetching ${endpointPath}:`, error);
            // this.errorMessage = `Failed to load data for ${endpointPath}. Please try again.`; // Optional
            throw error; // Re-throw to be caught by the calling method if needed
        } finally {
            // this.isLoading = false; // Optional: Clear loading state
        }
    },

    // --- Change Handlers ---
    async onVehicleTypeChange() {
      this.resetFieldsFrom('vehicleType');
      if (this.form.vehicleType) {
          try { await this.getBrands(); } catch (e) { /* Handle error if needed */ }
      }
      this.updateVehicleSlugUrl();
    },
    async onBrandChange() {
      this.resetFieldsFrom('brand');
      if (this.form.brand) {
          try { await this.getModels(); } catch (e) { /* Handle error */ }
      }
      this.updateVehicleSlugUrl();
    },
    async onModelChange() {
      this.resetFieldsFrom('model');
      if (this.form.model) {
          try { await this.getGenerations(); } catch (e) { /* Handle error */ }
      }
      this.updateVehicleSlugUrl();
    },
    async onGenerationChange() {
      this.resetFieldsFrom('generation');
      if (this.form.generation) {
          try {
              await this.getFuels(); // Fetch fuels (allows auto-select)
              await this.getEngines(); // Fetch engines based on generation/fuel
          } catch (e) { /* Handle error */ }
      }
      this.updateVehicleSlugUrl();
    },
    async onFuelChange() {
      // Only reset engine, but check if URL needs clearing
      const wasEngineSelected = !!this.form.engine;
      this.resetFieldsFrom('fuel'); // Resets engine form value and engines array
      if (this.form.generation) {
          try { await this.getEngines(); } catch (e) { /* Handle error */ }
      }
      // If an engine *was* selected, changing fuel invalidates it, clear URL
      if (wasEngineSelected) {
          this.updateVehicleSlugUrl();
      }
    },
    async onEngineChange() {
  // Find the selected engine object to get its database slug
  const selectedEngineObject = this.engines.find(e => String(e.id) === String(this.form.engine));

  // --- ADD THIS LOG ---
  console.log("[Live Debug] Found engine object in onEngineChange:", selectedEngineObject);
  // --- END LOG ---

  this.selectedEngineSlug = selectedEngineObject ? selectedEngineObject.slug : null;
  console.log("[Live Debug] Engine changed, selected DB engine slug:", this.selectedEngineSlug); // Keep this log too

  this.updateVehicleSlugUrl(); // Update URL based on selection/deselection

  // Emit current form state regardless of validity, parent component decides what to do
  this.$emit('getRemaps', { ...this.form });
},

    // --- API Fetch Methods (using fetchData helper) ---
    async getVehicleTypes() {
      console.log("Fetching Vehicle Types...");
      try {
          // Vehicle types endpoint might not need the proxy logic if it's simple
          // Adjust if necessary, or use fetchData('vdb/vehicle-types/dropdown')
          const response = await fetch(`${API_BASE}vdb/vehicle-types/dropdown`, { headers: { 'Content-Type': 'application/json' } });
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          const data = await response.json();
          this.vehicleTypes = Array.isArray(data) ? data : [];
          console.log("Vehicle Types:", this.vehicleTypes);
      } catch (error) {
          console.error("Error fetching vehicle types:", error);
          this.vehicleTypes = []; // Reset on error
          // throw error; // Re-throw if needed by created()
      }
    },
    async getBrands() {
      if (!this.form.vehicleType) { this.brands = []; return; }
      console.log("Fetching Brands for type:", this.form.vehicleType);
      try {
          const params = new URLSearchParams({ 'vehicle-type': this.form.vehicleType, 'hide-type': 1 });
          const data = await this.fetchData('vdb/brands/dropdown', params);
          this.brands = Array.isArray(data) ? data : [];
          console.log("Brands:", this.brands);
      } catch (error) { this.brands = []; /* Error already logged by fetchData */ }
    },
    async getModels() {
      if (!this.form.brand) { this.models = []; return; }
      console.log("Fetching Models for brand:", this.form.brand);
      try {
          const params = new URLSearchParams({ 'brand': this.form.brand, 'hide-brand': 1 });
          const data = await this.fetchData('vdb/models/dropdown', params);
          this.models = Array.isArray(data) ? data : [];
          console.log("Models:", this.models);
      } catch (error) { this.models = []; }
    },
    async getGenerations() {
      if (!this.form.model) { this.generations = []; return; }
      console.log("Fetching Generations for model:", this.form.model);
      try {
          const params = new URLSearchParams({ 'model': this.form.model, 'show-years': 1 });
          const data = await this.fetchData('vdb/generations/dropdown', params);
          this.generations = Array.isArray(data) ? data : [];
          console.log("Generations:", this.generations);
      } catch (error) { this.generations = []; }
    },
    async getFuels(preselecting = false) {
      const allFuelsOption = {id: null, name: 'All fuel types'};
      if (!this.form.generation) {
          this.fuels = [allFuelsOption];
          this.form.fuel = null;
          return;
      }
      console.log("Fetching Fuels for generation:", this.form.generation);
      try {
          const params = new URLSearchParams({ 'generation': this.form.generation });
          const data = await this.fetchData('vdb/fuel-types/dropdown', params);
          this.fuels = [allFuelsOption, ...(Array.isArray(data) ? data : [])];
          console.log("Fuels:", this.fuels);

          // Auto-select logic (only if not preselecting from URL)
          if (!preselecting && this.fuels.length === 2) { // Only 'All' + one actual type
              this.form.fuel = String(this.fuels[1].id);
          } else if (!preselecting) {
              this.form.fuel = null; // Default to 'All' if multiple options or error
          }
      } catch (error) {
          this.fuels = [allFuelsOption];
          this.form.fuel = null;
      }
    },
    async getEngines() {
      if (!this.form.generation) { this.engines = []; return; }
      console.log("Fetching Engines for generation/fuel:", this.form.generation, this.form.fuel);
      try {
          const params = new URLSearchParams({ generation: this.form.generation });
          if (this.form.fuel !== null && this.form.fuel !== undefined) {
            params.set('fuel', this.form.fuel);
          }
          const data = await this.fetchData('vdb/engines/dropdown', params);
          this.engines = Array.isArray(data) ? data : [];
          console.log("Engines fetched:", this.engines);
      } catch (error) { this.engines = []; }
    },

    /**
     * Updates the browser URL's query parameter 'vehicle' with a hierarchical slug
     * based on the current form selections, using underscore (_) as a separator.
     * Clears the parameter if the selection is incomplete or invalid.
     */

        updateVehicleSlugUrl() {
        // --- Add a clear marker for live debugging ---
        console.log("--- [Live Debug] updateVehicleSlugUrl START ---");

        if (this.isPrefilling) {
            console.log("[Live Debug] Skipping URL update during prefill.");
            console.log("--- [Live Debug] updateVehicleSlugUrl END (Prefilling) ---");
            return;
        }

        let hierarchicalSlug = null;
        console.log("[Live Debug] Attempting to build hierarchical slug...");

        // Construct slug only if a valid engine with a known DB slug is selected
        if (this.form.engine && this.selectedEngineSlug) {
            const selectedBrand = this.brands.find(b => String(b.id) === String(this.form.brand));
            const selectedModel = this.models.find(m => String(m.id) === String(this.form.model));
            const selectedGeneration = this.generations.find(g => String(g.id) === String(this.form.generation));

            // Log slugs for debugging
            console.log(`[Live Debug] Slugs - Brand: ${selectedBrand?.slug}, Model: ${selectedModel?.slug}, Gen: ${selectedGeneration?.slug}, Engine: ${this.selectedEngineSlug}`);

            if (selectedBrand?.slug && selectedModel?.slug && selectedGeneration?.slug && this.selectedEngineSlug) {
                hierarchicalSlug = `${selectedBrand.slug}_${selectedModel.slug}_${selectedGeneration.slug}_${this.selectedEngineSlug}`;
                console.log("[Live Debug] Successfully constructed hierarchical slug:", hierarchicalSlug);
            } else {
                console.warn("[Live Debug] FAILED to construct hierarchical slug - one or more slugs missing.");
            }
        } else {
             console.log("[Live Debug] Skipping hierarchical slug construction: Engine not selected or engine slug missing.");
        }

        // Update router query
        const currentSlugInUrl = this.$route.query.vehicle;
        const queryParams = { ...this.$route.query };

        console.log("[Live Debug] Final hierarchicalSlug value:", hierarchicalSlug);
        console.log("[Live Debug] Current URL slug:", currentSlugInUrl);

        if (hierarchicalSlug) {
            if (hierarchicalSlug !== currentSlugInUrl) {
                queryParams.vehicle = hierarchicalSlug;
                console.log("[Live Debug] Attempting to update URL with slug:", hierarchicalSlug, "Current query:", this.$route.query, "New query:", queryParams); // Log BEFORE replace
                this.$router.replace({ query: queryParams }).then(() => {
                    console.log("[Live Debug] Router replace SUCCESSFUL (Promise resolved). New route query:", this.$route.query); // Log success and check route state
                }).catch(err => {
                    if (err.name !== 'NavigationDuplicated') {
                        console.error("[Live Debug] Router replace FAILED:", err); // Log failure
                    } else {
                        console.log("[Live Debug] Router replace NavigationDuplicated (expected if slug didn't change)");
                    }
                });
            } else {
                 console.log("[Live Debug] URL slug is already correct.");
            }
        } else {
            // Clear slug from URL if it exists
            if (currentSlugInUrl) {
                console.log("[Live Debug] Attempting to clear vehicle slug from URL. Current query:", this.$route.query); // Log BEFORE replace
                delete queryParams.vehicle;
                this.$router.replace({ query: queryParams }).then(() => {
                     console.log("[Live Debug] Router replace SUCCESSFUL (cleared slug). New route query:", this.$route.query); // Log success
                }).catch(err => {
                     if (err.name !== 'NavigationDuplicated') {
                        console.error("[Live Debug] Router replace FAILED (clearing slug):", err); // Log failure
                     } else {
                         console.log("[Live Debug] Router replace NavigationDuplicated (clearing slug)");
                     }
                });
            } else {
                 console.log("[Live Debug] No slug to set and no current slug to clear.");
            }
        }
        console.log("--- [Live Debug] updateVehicleSlugUrl END ---");
    },


    /**
     * Attempts to prefill the form based on a 'vehicle' query parameter
     * containing a hierarchical slug (brand_model_gen_engine).
     */
    async prefillFromUrl() {
      const hierarchicalSlug = this.$route.query.vehicle;
      if (!hierarchicalSlug || typeof hierarchicalSlug !== 'string') {
        console.log("No valid vehicle slug found in URL query for prefill.");
        return;
      }
      console.log("Attempting prefill from hierarchical slug:", hierarchicalSlug);

      try {
        // 1. Lookup IDs from the full slug
        const params = new URLSearchParams({ 'slug': hierarchicalSlug });
        const ids = await this.fetchData('vdb/lookup-by-slug', params);

        if (!ids || ids.error || typeof ids !== 'object' || Object.keys(ids).length === 0) {
          console.warn("API lookup failed or returned invalid data for slug:", hierarchicalSlug, ids?.error || '');
          this.updateVehicleSlugUrl(); // Clear potentially invalid slug
          return;
        }
        console.log("Looked up IDs:", ids);

        // 2. Sequentially load and select dropdowns, validating each step
        // Vehicle Type
        if (!ids.vehicleTypeId || !this.vehicleTypes.some(vt => String(vt.id) === String(ids.vehicleTypeId))) {
            throw new Error(`VehicleType ID ${ids.vehicleTypeId} invalid or missing.`);
        }
        this.form.vehicleType = String(ids.vehicleTypeId);
        await this.getBrands();

        // Brand
        if (!ids.brandId || !this.brands.some(b => String(b.id) === String(ids.brandId))) {
            throw new Error(`Brand ID ${ids.brandId} invalid or missing.`);
        }
        this.form.brand = String(ids.brandId);
        await this.getModels();

        // Model
        if (!ids.modelId || !this.models.some(m => String(m.id) === String(ids.modelId))) {
            throw new Error(`Model ID ${ids.modelId} invalid or missing.`);
        }
        this.form.model = String(ids.modelId);
        await this.getGenerations();

        // Generation
        if (!ids.generationId || !this.generations.some(g => String(g.id) === String(ids.generationId))) {
            throw new Error(`Generation ID ${ids.generationId} invalid or missing.`);
        }
        this.form.generation = String(ids.generationId);
        await this.getFuels(true); // Fetch fuels, prevent auto-select

        // Fuel (Select if valid, otherwise default to 'All')
        if (ids.fuelId !== undefined && ids.fuelId !== null && this.fuels.some(f => String(f.id) === String(ids.fuelId))) {
            this.form.fuel = String(ids.fuelId);
        } else {
            this.form.fuel = null; // Default to 'All'
            if (ids.fuelId !== undefined && ids.fuelId !== null) {
                console.warn(`Fuel ID ${ids.fuelId} not found, defaulting to 'All'.`);
            }
        }
        await this.getEngines(); // Fetch engines based on generation/fuel

        // Engine
        const engineToSelect = this.engines.find(e => String(e.id) === String(ids.engineId));
        if (!engineToSelect) {
            throw new Error(`Engine ID ${ids.engineId} invalid or missing.`);
        }
        this.form.engine = String(engineToSelect.id);
        this.selectedEngineSlug = engineToSelect.slug; // Store the DB slug
        console.log("Stored engine slug from prefill:", this.selectedEngineSlug);

        // 3. Emit if successful
        if (this.isFormValid) {
            console.log("Prefill complete, emitting getRemaps.");
            this.$emit('getRemaps', { ...this.form });
        }
        console.log("Form state after successful prefill:", JSON.parse(JSON.stringify(this.form)));

      } catch (error) {
        console.error("Error during prefillFromUrl execution:", error.message || error);
        // If any step failed, reset the form and clear the URL slug
        this.resetFieldsFrom('vehicleType'); // Reset everything
        this.updateVehicleSlugUrl(); // Clear the invalid slug from URL
      }
    },

  } // End methods
}
</script>

<style lang="scss" scoped>
* {
  box-sizing: border-box;
}

.form-field-wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(270px, 1fr));
  margin-left: calc(-10px / 2);
  margin-right: calc(-10px / 2);
  margin-bottom: -10px;
}

.field-group {
  padding-right: 5px;
  padding-left: 5px;
  margin-bottom: 10px;
  width: 100%;
  flex-wrap: wrap;
  align-items: center;
  position: relative;
  display: flex;

  label {
    padding-bottom: 5px;
    font-family: "PT Sans", sans-serif;
    font-size: 16px;
    font-weight: 600;
    // Use CSS variable for color
    color: var(--erc-form-text-color, #ffffff); // Added fallback
  }

  &__button {
    align-items: flex-end;
  }
}

.form-field {
  font-family: "Open Sans", sans-serif;
  font-size: 16px;
  font-weight: 300;
  flex-basis: 100%;
  max-width: 100%;
  // Use CSS variable for color
  color: var(--erc-select-text-color, #000); // Added fallback
  display: flex;
  position: relative;
  width: 100%;
}

.select-wrapper {
  &::before {
    content: url('data:image/svg+xml; utf8, <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" class="svg-inline--fa fa-caret-down" data-icon="caret-down" data-prefix="fas" viewBox="0 0 320 512"><path fill="currentColor" d="m310.6 246.6-127.1 128c-7.1 6.3-15.3 9.4-23.5 9.4s-16.38-3.125-22.63-9.375l-127.1-128C.2244 237.5-2.516 223.7 2.438 211.8S19.07 192 32 192h255.1c12.94 0 24.62 7.781 29.58 19.75s3.12 25.75-6.08 34.85z"/></svg>');
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 10px;
    pointer-events: none;
    text-shadow: 0 0 3px rgba(0, 0, 0, .3);
    width: 10px;
    // Consider theme adjustment for arrow color if needed
    // color: var(--erc-select-text-color, #000);
  }

  select {
    // Use CSS variables
    background-color: var(--erc-select-bg-color, #ffffff);
    border: 1px solid var(--erc-select-border-color, #818a91);
    color: inherit; // Inherits from .form-field

    border-radius: 9px;
    flex-basis: 100%;
    appearance: none;
    font-size: inherit;
    font-family: inherit;
    font-weight: inherit;
    font-style: inherit;
    text-transform: inherit;
    letter-spacing: inherit;
    line-height: inherit;
    width: 100%;
    max-width: 100%;
    flex-grow: 1;
    min-height: 40px;
    padding: 5px 20px 5px 14px;
  }
}

.field-group__button {
    align-items: flex-end;
    padding-top: 10px;
}

.submit-button {
  // Use CSS variables
  background-color: var(--erc-button-bg-color, #ffffff);
  color: var(--erc-button-text-color, var(--erc-color-oem, #00aeff)); // Use OEM color variable

  flex-basis: 100%;
  min-height: 40px;
  border: none;
  cursor: pointer;
  display: inline-block;
  line-height: 1;
  padding: 0 24px;
  fill: #fff;
  text-align: center;
  transition: all .3s;
  border-radius: 10px !important;
  font-weight: 700;

  &:hover:enabled,
  &:focus:enabled {
    // Use CSS variables
    background-color: var(--erc-button-hover-bg-color, #00699b);
    color: var(--erc-button-hover-text-color, #fff);
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    // Consider theme variables for disabled state too
    background-color: #e3e3e3;
    color: #a7a7a7;
  }
}
</style>

